"use client";

import { useLayoutEffect, useState, type RefObject } from "react";

export type OverlayPlacement = "bottom-start" | "bottom-end" | "top-start" | "top-end" | "right-start";
export type OverlayPosition = {
  position: "fixed";
  left: number;
  top?: number;
  bottom?: number;
  minWidth?: number;
  width?: number;
  maxWidth?: number;
  maxHeight: number;
  zIndex: number;
};

export function useOverlayPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  placement: OverlayPlacement = "bottom-start",
  width: "anchor" | "content" | number = "content",
  gap = 8,
  overlayRef?: RefObject<HTMLElement | null>,
): OverlayPosition | null {
  const [style, setStyle] = useState<OverlayPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }

    function update() {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const gutter = 8;
      const resolvedWidth = width === "anchor" ? rect.width : typeof width === "number" ? width : undefined;
      const measuredWidth = width === "content" ? overlayRef?.current?.getBoundingClientRect().width : undefined;
      const positionedWidth = resolvedWidth ?? measuredWidth;
      const alignEnd = placement.endsWith("end");
      const wantsTop = placement.startsWith("top");
      const opensRight = placement === "right-start";
      const availableBelow = window.innerHeight - rect.bottom - gap - gutter;
      const availableAbove = rect.top - gap - gutter;
      const opensTop = wantsTop || (availableBelow < 180 && availableAbove > availableBelow);
      let left = opensRight ? rect.right + gap : alignEnd && positionedWidth ? rect.right - positionedWidth : rect.left;
      if (positionedWidth) left = Math.min(Math.max(gutter, left), Math.max(gutter, window.innerWidth - positionedWidth - gutter));
      else left = Math.max(gutter, Math.min(left, window.innerWidth - gutter));

      setStyle({
        position: "fixed",
        left,
        top: opensRight ? rect.top : opensTop ? undefined : rect.bottom + gap,
        bottom: opensRight ? undefined : opensTop ? window.innerHeight - rect.top + gap : undefined,
        minWidth: width === "content" && !opensRight ? rect.width : undefined,
        width: resolvedWidth,
        maxWidth: Math.max(0, window.innerWidth - gutter * 2),
        maxHeight: Math.max(0, opensRight ? window.innerHeight - rect.top - gutter : opensTop ? availableAbove : availableBelow),
        zIndex: 1200,
      });
    }

    update();
    const frame = window.requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const resizeObserver = typeof ResizeObserver !== "undefined" && overlayRef?.current
      ? new ResizeObserver(update)
      : null;
    if (overlayRef?.current) resizeObserver?.observe(overlayRef.current);
    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef, gap, open, overlayRef, placement, width]);

  return style;
}
